/**
 * Parses `<b>`/`<strong>`, `<u>`, `\n`/`<br>` into a node tree.
 * Other tags stay plain text (XSS-safe). Malformed markup never throws.
 *
 * Convention: markup must wrap the whole furigana `[base]{reading}` group.
 *   OK: `<u>[漢字]{かんじ}</u>`
 *   NG: `[<u>漢字</u>]{かんじ}` → splits the group, raw brackets are shown
 */

import { parseFurigana } from "@/utils/furigana";

export type RichTextMark = "bold" | "underline";

export type RichTextNode = { type: "text"; value: string } | { type: "break" } | { type: RichTextMark; children: RichTextNode[] };

const TAG_TO_MARK: Record<string, RichTextMark> = {
  b: "bold",
  strong: "bold",
  u: "underline",
};

// 1 = closing "/", 2 = tag name; other matches are line breaks
const TOKEN_RE = /<(\/?)(b|strong|u)\s*>|<br\s*\/?>|\\n|\r\n|\r|\n/gi;

type Frame = { mark: RichTextMark | null; children: RichTextNode[] };

export const parseRichText = (text: string | null | undefined): RichTextNode[] => {
  if (!text) return [];

  const root: Frame = { mark: null, children: [] };
  const stack: Frame[] = [root];
  const current = () => stack[stack.length - 1];

  const pushText = (value: string) => {
    if (value) current().children.push({ type: "text", value });
  };

  const closeTop = () => {
    const frame = stack.pop() as Frame;
    current().children.push({ type: frame.mark as RichTextMark, children: frame.children });
  };

  let lastIndex = 0;
  TOKEN_RE.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = TOKEN_RE.exec(text)) !== null) {
    pushText(text.slice(lastIndex, match.index));
    lastIndex = match.index + match[0].length;

    const [, closingSlash, tagName] = match;

    if (!tagName) {
      current().children.push({ type: "break" });
      continue;
    }

    const mark = TAG_TO_MARK[tagName.toLowerCase()];

    if (!closingSlash) {
      stack.push({ mark, children: [] });
      continue;
    }

    // Close up to nearest matching open tag; ignore stray closing tag
    const openIndex = stack.map((f) => f.mark).lastIndexOf(mark);
    if (openIndex <= 0) continue;
    while (stack.length > openIndex) closeTop();
  }

  pushText(text.slice(lastIndex));

  // Auto-close any unclosed tags
  while (stack.length > 1) closeTop();

  return root.children;
};

const nodesToText = (nodes: RichTextNode[]): string =>
  nodes
    .map((node) => {
      if (node.type === "text") return node.value;
      if (node.type === "break") return " ";
      return nodesToText(node.children);
    })
    .join("");

/**
 * Converts learning content to plain text for non-visual use (aria-label, title...).
 * Drops markup, turns line breaks into spaces, keeps only furigana base (`[漢字]{かんじ}` → `漢字`).
 */
export const toPlainText = (text: string | null | undefined): string => {
  const withoutMarkup = nodesToText(parseRichText(text));
  return parseFurigana(withoutMarkup)
    .map((segment) => (segment.type === "text" ? segment.value : segment.base))
    .join("")
    .replace(/\s+/g, " ")
    .trim();
};
